import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';
import { Camera, X, ArrowLeftRight, CheckCircle, AlertCircle, QrCode } from 'lucide-react';
import api from '../../services/api';
import { StatusBadge } from './estoque.utils';
import EstoqueMovimentarModal from './EstoqueMovimentarModal';

export default function EstoqueScanner() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const intervalRef = useRef(null);

  const [scanning, setScanning] = useState(false);
  const [item, setItem] = useState(null);
  const [pregerado, setPregerado] = useState(null);  // { token, numero_etiqueta }
  const [erro, setErro] = useState('');
  const [movOpen, setMovOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Se veio com token na URL (link do QR code), carrega direto
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) buscarPorToken(token);
  }, []);

  async function buscarPorToken(token) {
    setLoading(true); setErro(''); setItem(null); setPregerado(null);
    try {
      const { data } = await api.get(`/estoque/itens/qr/${token}`);
      if (data.status === 'vinculado') {
        setItem(data.data);
      } else if (data.status === 'pregerado') {
        setPregerado({ token, numero_etiqueta: data.numero_etiqueta });
      }
    } catch {
      setErro('QR Code não encontrado no sistema.');
    } finally { setLoading(false); }
  }

  async function iniciarCamera() {
    setErro(''); setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      intervalRef.current = setInterval(lerFrame, 300);
    } catch {
      setErro('Não foi possível acessar a câmera. Verifique as permissões.');
      setScanning(false);
    }
  }

  function pararCamera() {
    clearInterval(intervalRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
    }
    setScanning(false);
  }

  function lerFrame() {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState !== video.HAVE_ENOUGH_DATA) return;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    if (code?.data) {
      pararCamera();
      // Extrair token da URL do QR code
      try {
        const url = new URL(code.data);
        const token = url.searchParams.get('token');
        if (token) buscarPorToken(token);
        else setErro('QR Code inválido');
      } catch {
        setErro('QR Code inválido');
      }
    }
  }

  useEffect(() => () => pararCamera(), []);

  return (
    <div className="min-h-screen bg-gray-50 p-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">Scanner QR</h1>
        <button onClick={() => navigate('/estoque')} className="text-gray-400 hover:text-gray-600">
          <X size={22} />
        </button>
      </div>

      {/* Câmera */}
      <div className={`bg-black rounded-2xl overflow-hidden mb-4 ${scanning ? 'block' : 'hidden'}`} style={{ aspectRatio: '1' }}>
        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
        <canvas ref={canvasRef} className="hidden" />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-48 h-48 border-2 border-white rounded-2xl opacity-70" />
        </div>
      </div>

      {!scanning && !item && !pregerado && (
        <button onClick={iniciarCamera}
          className="w-full py-5 bg-blue-600 text-white rounded-2xl text-lg font-semibold flex items-center justify-center gap-3 hover:bg-blue-700 active:scale-95 transition-transform">
          <Camera size={28} /> Escanear QR Code
        </button>
      )}

      {scanning && (
        <button onClick={pararCamera}
          className="w-full py-4 border border-gray-300 text-gray-700 rounded-2xl text-base font-medium flex items-center justify-center gap-2 hover:bg-gray-50">
          <X size={20} /> Cancelar
        </button>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 mt-2 text-sm">Buscando item...</p>
        </div>
      )}

      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <p className="text-red-700 text-sm">{erro}</p>
        </div>
      )}

      {/* QR pré-gerado não vinculado */}
      {pregerado && !item && (
        <div className="bg-white rounded-2xl shadow-lg p-5 space-y-4 border-2 border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <QrCode size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="font-bold text-gray-800">Etiqueta: {pregerado.numero_etiqueta}</p>
              <p className="text-sm text-blue-600">QR gerado — ainda não vinculado a nenhuma ferramenta</p>
            </div>
          </div>
          <p className="text-sm text-gray-600">Esta etiqueta metálica está no sistema mas ainda não foi associada a nenhum item. Deseja cadastrar agora?</p>
          <div className="flex gap-3">
            <button onClick={() => { setPregerado(null); setErro(''); iniciarCamera(); }}
              className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
              <Camera size={16} /> Escanear outro
            </button>
            <button onClick={() => navigate(`/estoque/novo?qrtoken=${pregerado.token}`)}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 hover:bg-blue-700">
              Cadastrar ferramenta
            </button>
          </div>
        </div>
      )}

      {/* Card do item */}
      {item && (
        <div className="bg-white rounded-2xl shadow-lg p-5 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-mono text-sm font-bold text-gray-500">{item.codigo}</p>
              <h2 className="text-lg font-bold text-gray-800 leading-tight">{item.nome}</h2>
              {item.marca && <p className="text-sm text-gray-500">{item.marca}</p>}
            </div>
            <StatusBadge status={item.status} />
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-0.5">Obra / Local</p>
              <p className="font-semibold text-gray-800">{item.obra_atual || '—'}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-500 mb-0.5">Responsável</p>
              <p className="font-semibold text-gray-800">{item.responsavel_atual || '—'}</p>
            </div>
            {item.categoria_nome && (
              <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                <p className="text-xs text-gray-500 mb-0.5">Categoria</p>
                <p className="font-semibold text-gray-800">{item.categoria_nome}</p>
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={() => { setItem(null); setErro(''); iniciarCamera(); }}
              className="flex-1 py-3 border border-gray-200 text-gray-700 rounded-xl text-sm font-medium flex items-center justify-center gap-2">
              <Camera size={16} /> Escanear outro
            </button>
            <button onClick={() => setMovOpen(true)}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-700">
              <ArrowLeftRight size={16} /> Movimentar
            </button>
          </div>
        </div>
      )}

      {movOpen && item && (
        <EstoqueMovimentarModal
          item={item}
          onClose={() => setMovOpen(false)}
          onSuccess={async () => {
            const { data } = await api.get(`/estoque/itens/qr/${item.qrcode_token}`);
            setItem(data.data);
          }}
        />
      )}
    </div>
  );
}
